import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { inRange, isNull, omitBy } from 'lodash/fp';
import { useEffect } from 'react';
import { FieldErrors } from 'react-hook-form';
import styled from 'styled-components';

import {
  DEFAULT_MAX_CHAR_LENGTH,
  DEFAULT_SANITIZE,
  MAX_LENGTH_VALIDATION_MESSAGE,
  PANEL_FOOTER_HEIGHT,
} from 'dashboard/apps/InquiryTemplateEditor/FieldPanel/shared';
import DocumentFieldPanelContent from 'dashboard/components/DocumentFields/DocumentFieldPanelContent';
import { VerificationTemplateVersionDocument } from 'dashboard/models/verification-template-version';
import Button, { IconButton } from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import { FieldRedactionPolicy, FieldSchema, FieldSchemaType, FieldWritePolicy } from 'lib/fields';
import { useFormSecurely } from 'lib/security/reactHookForm';

const DEFAULT_FIELD_TYPE: FieldSchemaType = FieldSchemaType.String;
const DEFAULT_REDACTION_POLICY: FieldRedactionPolicy = 'none';
const DEFAULT_WRITE_POLICY: FieldWritePolicy = 'none';

const Form = styled.form`
  flex-direction: column;
  position: relative;
  width: 400px;
  height: 100%;
  overflow: hidden;
`;

const TitleContainer = styled.div`
  padding: ${Spacing[4]};
  padding-bottom: 0;
`;

const BodyContainer = styled.div`
  height: calc(100% - ${PANEL_FOOTER_HEIGHT}px);
  margin-bottom: ${PANEL_FOOTER_HEIGHT}px;
  overflow-y: auto;
`;

const FooterContainer = styled.footer`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 0px;
  height: ${PANEL_FOOTER_HEIGHT}px;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[5]};
`;

const errorRequired = { type: 'required', message: 'Required' };

const validateDocumentFieldPanelForm = (
  data: FieldSchema,
  documentFields: FieldSchema[],
  documentFieldIndex: number | null,
) => {
  const errors: FieldErrors<FieldSchema> = {};

  const usedDocumentFieldNames = documentFields
    .map((documentField) => documentField.label)
    .filter((_, index) => index !== documentFieldIndex);
  const usedDocumentFieldKeys = documentFields
    .map((documentField) => documentField.key)
    .filter((_, index) => index !== documentFieldIndex);

  if (data?.label == null || data?.label === '') {
    errors.label = errorRequired;
  } else if (usedDocumentFieldNames.includes(data.label)) {
    errors.label = {
      type: 'conflict',
      message: 'A document field with this label already exists. Please choose a different label.',
    };
  }
  if (usedDocumentFieldKeys.includes(data.key)) {
    errors.key = {
      type: 'conflict',
      message: 'A document field with this key already exists. Please choose a different key.',
    };
  }
  if (data?.type == null) {
    errors.type = errorRequired;
  }
  switch (data?.type) {
    case FieldSchemaType.Boolean:
      break;
    case FieldSchemaType.Integer:
      if (
        data?.['default-value'] != null &&
        data?.['default-value'] !== '' &&
        !Number.isInteger(data?.['default-value'])
      ) {
        errors['default-value'] = {
          type: 'integer',
          message: 'Must be an integer',
        };
      }
      break;
    case FieldSchemaType.Number:
      break;
    case FieldSchemaType.String:
      if (
        data?.config['max-char-length'] != null &&
        !inRange(1, DEFAULT_MAX_CHAR_LENGTH + 1, data?.config['max-char-length'])
      ) {
        errors.config = {
          ...errors.config,
          'max-char-length': {
            type: 'range',
            message: MAX_LENGTH_VALIDATION_MESSAGE,
          },
        };
      }
      break;
  }

  return Object.keys(errors).length === 0
    ? { values: data, errors: {} }
    : { values: {}, errors: errors };
};

interface DocumentFieldPanelProps {
  vtmplVersion: VerificationTemplateVersionDocument;
  // index of currently selected document field (null if new document field)
  documentFieldIndex: number | null;
  closeDocumentField: () => void;
  addDocumentField: (documentField: FieldSchema) => void;
  updateDocumentField: (documentField: FieldSchema) => void;
  deleteDocumentField: () => void;
}

const DocumentFieldPanel = ({
  vtmplVersion,
  documentFieldIndex,
  closeDocumentField,
  addDocumentField,
  updateDocumentField,
  deleteDocumentField,
}: DocumentFieldPanelProps) => {
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        closeDocumentField();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [closeDocumentField]);

  const documentFields = vtmplVersion.attributes['field-schemas'] || [];

  const isNew = documentFieldIndex == null;
  let defaultValues: FieldSchema = {
    label: '',
    key: '',
    type: DEFAULT_FIELD_TYPE,
    'default-value': null,
    config: {
      'max-char-length': DEFAULT_MAX_CHAR_LENGTH,
      sanitize: DEFAULT_SANITIZE,
      'redaction-policy': DEFAULT_REDACTION_POLICY,
      'write-policy': DEFAULT_WRITE_POLICY,
    },
  };

  if (!isNew) {
    defaultValues = {
      ...defaultValues,
      ...documentFields[documentFieldIndex],
    };
  }

  const formReturn = useFormSecurely<FieldSchema>({
    defaultValues,
    resolver: (data) => validateDocumentFieldPanelForm(data, documentFields, documentFieldIndex),
    mode: 'onChange',
  });
  const { handleSubmit } = formReturn;

  const handleSubmitForm = handleSubmit((data) => {
    // NB: this removes any keys not relevant for the specific type so that it can be cast
    //     to the correct type when saved to the backend
    //   e.g., removes 'max-char-length' for any type that isn't FieldSchemaType.String
    data.config = omitBy(isNull, data.config);

    if (data.type === FieldSchemaType.Array) {
      data.config['item-schema'] = {
        key: data.key,
        label: null as never,
        type: FieldSchemaType.String,
        config: {},
      };
    }

    if (isNew) {
      addDocumentField(data);
    } else {
      updateDocumentField(data);
    }
    closeDocumentField();
  });

  const handleCancel = () => {
    closeDocumentField();
  };

  const handleDelete = () => {
    deleteDocumentField();
    closeDocumentField();
  };

  return (
    <Form id='document-field-panel__form' onSubmit={handleSubmitForm}>
      <BodyContainer>
        <TitleContainer>
          <Paragraph size='lg' weight='bold' color='primary'>
            {isNew ? 'Add' : 'Edit'} document field
          </Paragraph>
        </TitleContainer>
        <DocumentFieldPanelContent formReturn={formReturn} />
      </BodyContainer>
      <FooterContainer>
        <HStack style={{ flexGrow: 1 }}>
          <Button
            id={`document-field-panel__${isNew ? 'add' : 'update'}-button`}
            type='submit'
            width='auto'
            onClick={handleSubmitForm}
          >
            {isNew ? 'Add' : 'Update'}
          </Button>
          <Button
            id='document-fiel-panel__close-button'
            width='auto'
            variant='basic'
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </HStack>
        <IconButton
          id='document-field-panel__delete-button'
          onClick={handleDelete}
          disabled={isNew}
        >
          <TrashIcon />
        </IconButton>
      </FooterContainer>
    </Form>
  );
};

export default DocumentFieldPanel;
